import { cloneElement } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTranslate } from 'react-admin';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import RoomIcon from '@mui/icons-material/Room';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import renderDangerousText from '@/utils/renderDangerousText';
import { toFormatDate } from '@/utils/date';

export const DATA_SOURCE_BACKOFFICE = 'backoffice';

const CustomTypography = styled(Typography)({
    fontSize: 12,
    opacity: '54%',
    color: 'black',
});

const renderIcon = (dataSource: string) =>
    dataSource.toLowerCase() === DATA_SOURCE_BACKOFFICE.toLowerCase() ? <DesktopWindowsIcon /> : <PhoneAndroidIcon />;

const Dot = () => <CustomTypography>·</CustomTypography>;

const Item = ({ tooltipText, icon }: { icon: any; tooltipText?: string }) => {
    const outIcon = cloneElement(icon, {
        sx: {
            fontSize: 15,
            color: '#757575',
        },
    });

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, auto)',
                alignItems: 'center',
            }}
        >
            <Dot />

            {tooltipText ? <Tooltip title={tooltipText}>{outIcon}</Tooltip> : outIcon}
        </Box>
    );
};

interface Props {
    title: any;
    icon: any;
    time: string;
    authInfo?: {
        firstName?: string;
        lastName?: string;
        username?: string;
    };
    coords?: {
        latitude: number;
        longitude: number;
        addressText?: string;
    };
    dataSource?: string;
    visible?: boolean;
}

const NoteTitle = ({ title, icon, time, authInfo, dataSource, coords, visible }: Props) => {
    const translate = useTranslate();

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                alignItems: 'center',
                gap: 1,
                px: 1,
            }}
        >
            {cloneElement(icon, {
                color: 'primary',
                sx: {
                    fontSize: 40,
                },
            })}

            <Box
                sx={{
                    display: 'grid',
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 'bold',
                        textTransform: 'capitalize',
                    }}
                >
                    {renderDangerousText(title)}
                </Typography>

                <Box
                    sx={{
                        display: 'grid',
                        gap: 0.5,
                        gridTemplateColumns: `auto ${authInfo ? 'auto' : ''} auto`,
                        justifyContent: 'start',
                        alignItems: 'center',
                    }}
                >
                    <CustomTypography>{toFormatDate(time)}</CustomTypography>

                    {authInfo && (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, auto)',
                                alignItems: 'center',
                                gap: 0.5,
                            }}
                        >
                            <Dot />

                            <CustomTypography>{translate('app.notes.makeBy')}</CustomTypography>

                            <CustomTypography noWrap>
                                <b>{authInfo?.firstName ?? authInfo?.username}</b>
                            </CustomTypography>
                        </Box>
                    )}

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        {dataSource && <Item icon={renderIcon(dataSource)} />}

                        {coords && <Item icon={<RoomIcon />} />}

                        {visible !== undefined && !visible && <Item icon={<RemoveCircleOutlineIcon />} />}
                    </Box>
                </Box>

                {coords?.addressText && (
                    <CustomTypography
                        style={{
                            fontSize: 12,
                            opacity: '54%',
                            color: 'black',
                        }}
                    >
                        {coords.addressText}
                    </CustomTypography>
                )}
            </Box>
        </Box>
    );
};

export default NoteTitle;
